import { useCallback, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import { IPaymentDetails, RequestPaymentFn, paymentErrorType } from 'features/payment/types';
import { isPayPal, isVenmo } from 'features/payment/utils';

import { getConfigValue } from 'utils/environment';
import { logger } from 'utils/logger';

import { useBraintreeInitialization } from './use-braintree-initialization.web';

export enum FlowType {
  /**
   * Used to store the payment method for future use, ie subscriptions
   */
  Vault = 'vault',

  /**
   * Used for one-time checkout
   */
  Checkout = 'checkout',
}

export enum Intent {
  /**
   * Submits the transaction for authorization but not settlement.
   */
  Authorize = 'authorize',

  /**
   * Validates the transaction without an authorization (i.e. without holding funds).
   * Useful for authorizing and capturing funds up to 90 days after the order has been placed.
   * Only available for Checkout flow.
   */
  Order = 'order',

  /**
   * Payment will be immediately submitted for settlement upon creating a transaction.
   * `sale` can be used as an alias for this value.
   */
  Capture = 'capture',
}

export const useRequestBraintreePayment: RequestPaymentFn = () => {
  const { braintreeAuthorizationKey, braintreeBillingAgreementDescription } = getConfigValue({
    key: 'firstData',
    defaultValue: {},
  });
  const { checkoutPaymentMethod } = usePaymentContext();
  const isPayPalPayment = isPayPal(checkoutPaymentMethod?.accountIdentifier);
  const isVenmoPayment = isVenmo(checkoutPaymentMethod?.accountIdentifier);
  const isBraintreePayment = isPayPalPayment || isVenmoPayment;

  const [data, setData] = useState<IPaymentDetails>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<paymentErrorType>();

  const { braintreeInstances, isInitializing } = useBraintreeInitialization({
    isBraintreePayment,
    isPayPalPayment,
    isVenmoPayment,
    braintreeAuthorizationKey,
  });

  const requestPayment = useCallback(async () => {
    if (!isBraintreePayment || !braintreeInstances.client) {
      return;
    }

    if (isPayPalPayment && braintreeInstances.paypalCheckout) {
      try {
        setIsLoading(true);
        const paymentData = {
          flow: FlowType.Vault,
          currency: 'USD',
          intent: Intent.Capture,
          requestBillingAgreement: true,
          billingAgreementDescription: braintreeBillingAgreementDescription,
          enableShippingAddress: false,
          shippingAddressEditable: false,
        };

        const payload = await braintreeInstances.paypalCheckout.tokenize(paymentData);
        const deviceData = JSON.parse(braintreeInstances.dataCollector.deviceData).correlation_id;
        const details = JSON.stringify(payload.details);
        const nonce = payload.nonce;

        setData({
          paypalDetails: {
            nonce,
            deviceData,
            details,
          },
        });
      } catch (e) {
        if (e && typeof e === 'object' && 'code' in e && e.code === 'PAYPAL_POPUP_CLOSED') {
          return;
        }
        logger.error({
          message:
            '[useRequestBraintreePayment][requestPayment] Error occurred while requesting Paypal payment.',
          isPayPalPayment,
          isVenmoPayment,
          e,
        });
        setError(paymentErrorType.genericError);
      } finally {
        setIsLoading(false);
      }
    } else if (isVenmoPayment && braintreeInstances.venmo) {
      try {
        setIsLoading(true);
        const payload = await braintreeInstances.venmo.tokenize();
        const deviceData = JSON.parse(braintreeInstances.dataCollector.deviceData).correlation_id;
        const details = JSON.stringify(payload.details);
        const nonce = payload.nonce;

        setData({
          venmoDetails: {
            nonce,
            deviceData,
            details,
          },
        });
      } catch (e) {
        if (e && typeof e === 'object' && 'code' in e && e.code === 'VENMO_CANCELED') {
          return;
        }
        logger.error({
          message:
            '[useRequestBraintreePayment][requestPayment] Error occurred while requesting Venmo payment.',
          isPayPalPayment,
          isVenmoPayment,
          e,
        });
        setError(paymentErrorType.genericError);
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    isBraintreePayment,
    isPayPalPayment,
    isVenmoPayment,
    braintreeInstances,
    braintreeBillingAgreementDescription,
  ]);

  return {
    data,
    isLoading: isLoading || isInitializing,
    error,
    requestPayment,
  };
};
